import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { PageHeader } from '../components/common';
import { ROUTES } from '../constants';
import { snapEarnService, ApiError, reversalService } from '../services';
import moment from 'moment';

const ReversalTransactionDetailPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const imageRef = useRef(null);

  // Get transaction from state or use dummy data
  const transactionFromState = location.state?.transaction;

  // Merge transaction data - use receiptImages from API if available
  const mergeTransactionData = transaction => {
    if (!transaction) return dummyTransaction;

    return {
      ...transaction,
      // Use receipt images from API if available, otherwise empty array
      receiptImages: transaction.receiptImages || [],
    };
  };

  // State management
  const [transactionData, setTransactionData] = useState(
    mergeTransactionData(transactionFromState || dummyTransaction)
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  // Determine initial status based on transactionData
  // transactionData.status comes from service transform which returns 'Approved', 'Pending', 'Rejected'

  const [cancelReason, setCancelReason] = useState(
    transactionData.rejectedReasonId || ''
  );

  const [saving, setSaving] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [otherReasonDescription, setOtherReasonDescription] = useState(
    transactionData.rejectedReasonDescription || ''
  );

  // Image drag state
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Reject reason options from API
  const [cancelReasons, setCancelReasons] = useState([]);
  const [loadingReasons, setLoadingReasons] = useState(false);

  // Fetch rejected reasons from API
  const fetchRejectedReasons = useCallback(async () => {
    setLoadingReasons(true);
    try {
      const reasons = await reversalService.getFormattedCancelReasons();
      setCancelReasons(reasons);
    } catch (err) {
      console.error('Failed to fetch rejected reasons:', err);
      // Fallback to empty array on error - dropdown will show no options
      setCancelReasons([]);
    } finally {
      setLoadingReasons(false);
    }
  }, []);

  // Fetch rejected reasons on component mount
  useEffect(() => {
    fetchRejectedReasons();
  }, [fetchRejectedReasons]);

  // Effect to set reject reason specific logic once reasons are loaded
  useEffect(() => {
    if (cancelReasons.length > 0) {
      // If we have a description but no ID (or ID not in list), try to find 'Other'
      if (transactionData.rejectedReasonDescription && !cancelReason) {
        const otherReason = cancelReasons.find(r => r.description === 'Other');
        if (otherReason) {
          setCancelReason(otherReason.id);
        }
      }
    }
  }, [cancelReasons, transactionData, cancelReason]);

  // Image navigation handlers
  const handlePreviousImage = () => {
    setCurrentImageIndex(prev =>
      prev === 0 ? transactionData.receiptImages.length - 1 : prev - 1
    );
    setZoomLevel(1);
    setDragPosition({ x: 0, y: 0 });
  };

  const handleNextImage = () => {
    setCurrentImageIndex(prev =>
      prev === transactionData.receiptImages.length - 1 ? 0 : prev + 1
    );
    setZoomLevel(1);
    setDragPosition({ x: 0, y: 0 });
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => {
      const newZoom = Math.max(prev - 0.25, 1);
      if (newZoom === 1) {
        setDragPosition({ x: 0, y: 0 });
      }
      return newZoom;
    });
  };

  // Drag handlers
  const handleMouseDown = e => {
    if (zoomLevel > 1) {
      e.preventDefault();
      setIsDragging(true);
      setDragStart({
        x: e.clientX - dragPosition.x,
        y: e.clientY - dragPosition.y,
      });
    }
  };

  const handleMouseMove = e => {
    if (isDragging && zoomLevel > 1 && imageRef.current) {
      e.preventDefault();

      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;

      // Calculate bounds
      // The image is scaled from the center, so the maximum translation in either direction
      // is half of the total overflow width/height.
      // overflowWidth = (width * zoomLevel) - width = width * (zoomLevel - 1)
      // maxTranslate = overflowWidth / 2
      const { offsetWidth: width, offsetHeight: height } = imageRef.current;
      const maxTranslateX = (width * (zoomLevel - 1)) / 2;
      const maxTranslateY = (height * (zoomLevel - 1)) / 2;

      // Clamp values
      const clampedX = Math.min(Math.max(newX, -maxTranslateX), maxTranslateX);
      const clampedY = Math.min(Math.max(newY, -maxTranslateY), maxTranslateY);

      setDragPosition({
        x: clampedX,
        y: clampedY,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  // Form handlers

  const handleCancel = () => {
    navigate(ROUTES.REVERSAL);
  };

  const [isQuotaExceededError, setIsQuotaExceededError] = useState(false);

  // Check if save button should be enabled
  const isSaveEnabled = () => {
    // If transaction is already approved, disable save
    if (transactionData.status === 'Cancelled' || transactionData.status === 'C')
      return false;
    if (!cancelReason) return false;

    const selectedReason = cancelReasons.find(r => r.id == cancelReason);
    if (
      selectedReason?.description === 'Other' &&
      !otherReasonDescription.trim()
    ) {
      return false;
    }

    return true;
  };

  // Format amount with . as decimal separator
  const formatAmount = amount => {
    return amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setIsQuotaExceededError(false);

    try {
      // Map status to API format (A: Approve, R: Reject)
      const payload = {
        id: transactionData.id,
      };

      // Add reason if rejected

      payload.cancelReasonId = cancelReason;
      const selectedReason = cancelReasons.find(r => r.id == cancelReason);
      payload.cancelReasonDescription = selectedReason?.description;
      if (selectedReason && selectedReason.description === 'Other') {
        payload.cancelReasonDescription = otherReasonDescription;
      }


      await reversalService.processCancellation(payload);

      // Show success message (in production, use toast/notification)
      alert('Transaction updated successfully!');

      // Navigate back to list
      navigate(ROUTES.REVERSAL);
    } catch (err) {
      console.error('Failed to update transaction:', err);

      let errorMessage = 'Failed to update transaction. Please try again.';

      if (err instanceof ApiError) {
        errorMessage = err.message;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      // Handle Quota exceeded specifically
      if (
        errorMessage &&
        errorMessage.toLowerCase().includes('quota exceeded')
      ) {
        setTransactionStatus('reject');

        // Find reason for "Quota Exceeded"
        const quotaReason = cancelReasons.find(
          r => r.description.toLowerCase() === 'quota exceeded'
        );
        if (quotaReason) {
          setCancelReason(quotaReason.id);
        }

        setIsQuotaExceededError(true);
        // Use setTimeout to allow UI to update (radio and dropdown) before alert blocks execution
        setTimeout(() => {
          alert('Quota Exceeded');
          navigate(ROUTES.ONE_SNAP);
        }, 100);
      }

      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };
  return (
    <div>
      <PageHeader
        title='Edit Transaction'
        breadcrumbs={[
          { name: 'Home', href: ROUTES.DASHBOARD },
          { name: 'Reversal', href: ROUTES.REVERSAL },
          { name: 'Edit Transaction' },
        ]}
      />

      {/* Error Display */}
      {error && (
        <div className='bg-red-50 border border-red-200 rounded-lg p-4 mb-6'>
          <div className='flex items-center'>
            <svg
              className='w-5 h-5 text-red-400 mr-2'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z'
              />
            </svg>
            <p className='text-sm text-red-700'>{error}</p>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className='bg-white rounded-lg shadow-sm border border-gray-200'>
        <div className='grid grid-cols-1 lg:grid-cols-5 gap-6 p-6'>
          {/* Left Section - Receipt Images */}
          <div className='lg:col-span-2'>
            <div className='bg-gray-100 rounded-lg p-4'>
              {/* Image Display */}
              <div
                className='relative bg-white rounded-lg overflow-hidden mb-4'
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
              >
                {transactionData.receiptImages &&
                transactionData.receiptImages.length > 0 ? (
                  <img
                    ref={imageRef}
                    src={transactionData.receiptImages[currentImageIndex]}
                    alt={`Receipt ${currentImageIndex + 1}`}
                    onMouseDown={handleMouseDown}
                    className={`w-full h-auto ${
                      isDragging ? '' : 'transition-transform duration-200'
                    }`}
                    style={{
                      transform: `translate(${dragPosition.x}px, ${dragPosition.y}px) scale(${zoomLevel})`,
                      cursor:
                        zoomLevel > 1
                          ? isDragging
                            ? 'grabbing'
                            : 'grab'
                          : 'default',
                    }}
                  />
                ) : (
                  <div className='flex items-center justify-center h-96 bg-gray-200'>
                    <p className='text-gray-500'>No receipt image available</p>
                  </div>
                )}

                {/* Zoom Icons */}
                <div className='absolute bottom-4 right-4 flex gap-2'>
                  <button
                    onClick={handleZoomIn}
                    className='bg-white rounded-full p-2 shadow-lg hover:bg-gray-50'
                    title='Zoom in'
                  >
                    <svg
                      className='w-5 h-5 text-gray-700'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7'
                      />
                    </svg>
                  </button>
                  <button
                    onClick={handleZoomOut}
                    className='bg-white rounded-full p-2 shadow-lg hover:bg-gray-50'
                    title='Zoom out'
                  >
                    <svg
                      className='w-5 h-5 text-gray-700'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7'
                      />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Image Counter & Navigation */}
              {transactionData.receiptImages &&
                transactionData.receiptImages.length > 1 && (
                  <div className='flex items-center justify-between'>
                    <button
                      onClick={handlePreviousImage}
                      className='p-2 rounded-full hover:bg-gray-200'
                    >
                      <svg
                        className='w-6 h-6 text-gray-700'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M15 19l-7-7 7-7'
                        />
                      </svg>
                    </button>

                    <span className='text-sm text-gray-600'>
                      image {currentImageIndex + 1} of{' '}
                      {transactionData.receiptImages.length}
                    </span>

                    <button
                      onClick={handleNextImage}
                      className='p-2 rounded-full hover:bg-gray-200'
                    >
                      <svg
                        className='w-6 h-6 text-gray-700'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M9 5l7 7-7 7'
                        />
                      </svg>
                    </button>
                  </div>
                )}
            </div>
          </div>

          {/* Right Section - Form Fields */}
          <div className='lg:col-span-3 space-y-4'>
            {/* Company Name (Read-only) */}
            <div>
              <label className='block text-sm font-medium text-gray-500 mb-1'>
                Company name
              </label>
              <div className='bg-gray-200 px-3 py-2 rounded-md border border-gray-200'>
                <p className='text-sm text-gray-500'>
                  {transactionData.companyName}
                </p>
              </div>
            </div>

            {/* Sector & Outlet (Read-only, side by side) */}
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-500 mb-1'>
                  Sector
                </label>
                <div className='bg-gray-200 px-3 py-2 rounded-md border border-gray-200'>
                  <p className='text-sm text-gray-500'>
                    {transactionData.sector}
                  </p>
                </div>
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-500 mb-1'>
                  Outlet
                </label>
                <div className='bg-gray-200 px-3 py-2 rounded-md border border-gray-200'>
                  <p className='text-sm text-gray-500'>
                    {transactionData.outlet}
                  </p>
                </div>
              </div>
            </div>

            {/* Shopper Name (Read-only) */}
            <div>
              <label className='block text-sm font-medium text-gray-500 mb-1'>
                Shopper name
              </label>
              <div className='bg-gray-200 px-3 py-2 rounded-md border border-gray-200'>
                <p className='text-sm text-gray-500'>
                  {transactionData.shopperName}
                </p>
              </div>
            </div>

            {/* Shopper Phone Number (Read-only) */}
            <div>
              <label className='block text-sm font-medium text-gray-500 mb-1'>
                Shopper phone number
              </label>
              <div className='flex gap-2'>
                <div className='bg-gray-200 px-3 py-2 rounded-md border border-gray-200 flex-1'>
                  <p className='text-sm text-gray-500'>
                    {transactionData.shopperPhone.replace('+62', '')}
                  </p>
                </div>
              </div>
            </div>

            {/* Transaction Date & Amount (Editable, side by side) */}
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Transaction date <span className='text-red-500'>*</span>
                </label>
                <div className='bg-gray-200 px-3 py-2 rounded-md border border-gray-200'>
                  <p className='text-sm text-gray-500'>
                    {transactionData.transactionDate}
                  </p>
                </div>                
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-1'>
                  Transaction amount <span className='text-red-500'>*</span>
                </label>
                <div className='bg-gray-200 px-3 py-2 rounded-md border border-gray-200'>
                  <p className='text-sm text-gray-500'>
                    {transactionData.transactionAmount}
                  </p>
                </div>               
              </div>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>
                Reason
              </label>
              {transactionData.status == 'Cancelled' &&
              <div className='bg-gray-200 px-3 py-2 rounded-md border border-gray-200'>
                  <p className='text-sm text-gray-500'>
                    {transactionData.cancelledReasonDescription}
                  </p>
              </div> 
              }
              {transactionData.status != 'Cancelled' &&
              <select
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
                disabled={loadingReasons}
                className='block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                <option value=''>
                  {loadingReasons ? 'Loading reasons...' : 'Select reason...'}
                </option>
                {cancelReasons.map(reason => (
                  <option key={reason.id} value={reason.id}>
                    {reason.description}
                  </option>
                ))}
              </select>
              }
              {cancelReasons.find(r => r.id == cancelReason)?.description ===
                'Other' && (
                <div className='mt-3'>
                  <label className='block text-sm font-medium text-gray-700 mb-1'>
                    Short Description
                  </label>
                  <input
                    type='text'
                    value={otherReasonDescription}
                    onChange={e => setOtherReasonDescription(e.target.value)}
                    placeholder='Enter a short description...'
                    className='block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                  />
                </div>
              )}              
            </div>
              

            <div className='pt-4 border-t border-gray-200'>
              <h3 className='text-sm font-medium text-gray-900 mb-3'>
                Transaction History
              </h3>
              <div className='border border-gray-200 rounded-lg p-4'>
                <div className='relative'>

                  {transactionData.histories.slice().reverse().map((history, index) => (
                    // The 'key' prop is essential for list rendering
                    <div className='flex items-start gap-3 pb-6 relative'>
                      {/* Vertical line connector */}
                      {index < transactionData.histories.length - 1 && (
                        <div className='absolute left-3 top-6 bottom-0 w-px bg-gray-300'></div>
                      )}
                      {index == 0 && (
                        <div className='flex-shrink-0 w-6 h-6 rounded bg-green-500 flex items-center justify-center relative z-10'>
                          <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6.66732 2L3.00065 5.66667L1.33398 4" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                      )}
                      {index > 0 && (
                        <div className='flex-shrink-0 w-6 h-6 rounded bg-gray-300 flex items-center justify-center relative z-10'>
                          <div className='w-2 h-2 rounded-full bg-gray-500'></div>
                        </div>
                      )}
                      <div className='flex-1'>
                        <p className='text-sm text-gray-900'>
                          {history.action} - by [{history.by}]
                        </p>
                        <p className='text-xs text-gray-500 mt-0.5'>
                          {history.at ? moment(history.at).format('D MMM YYYY • HH:mm') : 'N/A'}
                        </p>
                      </div>
                    </div>
                  ))}            
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className='flex gap-3 pt-6'>
              <button
                onClick={handleCancel}
                disabled={saving}
                className='flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!isSaveEnabled() || saving}
                className='flex-1 px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReversalTransactionDetailPage;
